import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { BoldMessageText } from "../../../components/Icon";
import Profile from "../../../assets/images/profile1.png";
import { useTranslation } from "react-i18next";
import { NotificationByUserView } from "../../../apis";
import {
  alpha,
  AppBar,
  Avatar,
  Box,
  Card,
  CardContent,
  CardMedia,
  Container,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
  Zoom,
} from "@mui/material";
import { EGIconButton } from "../../../components/MainComponents/Button/IconButton/IconButton";
import { EGButton } from "../../../components/MainComponents/Button/EGButton/EGButton";
import { Notifications } from "../../../components/GlobalComponents/Notifications/Notifications";
import Icon from "../../../components/Icon/Icon";
import { startLoggedOut } from "../../../store/auth/authThunks";
import { GeneralSearchBar } from "../../../components/GlobalComponents/Search/GeneralSearchBar";
import { getLogo } from "../../../helpers/getLogo";
import { startSetTheme } from "../../../store/theme/themeThunks";

const EGLogo = getLogo();

export const Navbar = ({
  isMobile,
  isDashboard,
  showSidebar,
  setShowSidebar,
}) => {
  // Custom hooks
  const [t] = useTranslation("components");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  // Initial state
  const [notificationData, setNotificationData] = useState(null);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  // Open modal profile
  const toggleMenu = (e) => {
    if (!e && !anchorEl) return;
    setAnchorEl(e ? e.currentTarget : null);
  };
  // Logout
  const handleLogout = () => {
    dispatch(startLoggedOut());
    navigate("/login");
  };
  // Menu items
  const menuItems = [
    "divider",
    {
      icon: "outline_edit_2",
      label: t("Navbar.edit_profile"),
      onClick: () => navigate("/profile"),
    },
    {
      icon: "outline_logout",
      label: t("Navbar.logout"),
      onClick: () => handleLogout(),
    },
  ];
  // Menu structure
  const MenuItemWithIcon = ({ icon, label, onClick }) => (
    <MenuItem
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <ListItemIcon sx={{ minWidth: 0 }}>
        <Icon name={icon} color={theme.palette.primary.main} />
      </ListItemIcon>
      <Typography variant="body1">{label}</Typography>
    </MenuItem>
  );

  useEffect(() => {
    rechargeData();
  }, [location]);

  // Request for notifications
  const rechargeData = async (notifications = 20) => {
    try {
      const response = await NotificationByUserView(notifications);
      setNotificationData(response.results);
      // Count and update unread notifications
      const notificationsUnread = response.results.filter(
        (n) => !n.deleted_at
      ).length;
      setUnreadNotifications(notificationsUnread);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + (isMobile ? -1 : 1),
        boxShadow: "none",
        bgcolor: "background.paper",
      }}
    >
      <Container maxWidth={"false"}>
        <Toolbar
          disableGutters
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {isMobile ? (
            <Icon name="imagotipo_ethics_4" size={31} />
          ) : (
            <CardMedia sx={{ mr: 1, ml: -1 }}>
              <img src={EGLogo} alt="Logo" style={{ maxHeight: 35.5, width: "auto" }} />
            </CardMedia>
          )}

          {(isMobile || isDashboard) && (
            <EGIconButton
              iconSize={25}
              title={
                showSidebar ? t("Sidebar.close_menu") : t("Sidebar.open_menu")
              }
              placement="bottom"
              iconColor="secondary"
              iconName="hamburguer_menu"
              onClick={() => setShowSidebar((prev) => !prev)}
              sx={{ mr: 1, ml: 0.5 }}
            />
          )}

          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              ml: { md: 1 },
            }}
          >
            <GeneralSearchBar />
          </Box>

          <Stack
            sx={{ alignItems: "center", flexFlow: "row nowrap" }}
            spacing={{ xs: 0.5, sm: 1.5, md: 3 }}
          >
            <Notifications
              notificationData={notificationData}
              setNotificationData={setNotificationData}
              rechargeData={rechargeData}
              unreadNotifications={unreadNotifications}
            />

            <EGIconButton
              iconSize={26}
              title={t("Navbar.settings")}
              placement="bottom"
              iconColorHover="white"
              colorHover="primary"
              iconColor="secondary"
              iconName="settings"
              onClick={() => navigate("/settings")}
            />

            {!isMobile && (
              <EGButton
                color="primary"
                label={t("Navbar.new_report")}
                size="medium"
                onClick={() => navigate("/reports/new")}
                sx={{
                  px: 4,
                  "&:hover": {
                    boxShadow: `0px 0px 10px -2px ${theme.palette.primary.light}`,
                  },
                }}
              />
            )}
            <Tooltip
              title={t("Navbar.menu")}
              arrow
              TransitionComponent={Zoom}
              TransitionProps={{ timeout: 150 }}
            >
              <IconButton
                onClick={toggleMenu}
                sx={{ p: 0 }}
                aria-controls={open ? "account-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
              >
                <Avatar alt={t("Navbar.profile")} src={Profile} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Menu
            anchorEl={anchorEl}
            id="accoun-menu"
            open={open}
            onClose={() => toggleMenu(null)}
            onClick={() => toggleMenu(null)}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1.5,
                  px: 0.5,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  "&::before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <a
              href="https://webservice.atlassian.net/servicedesk/customer/portal/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <Card
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  borderRadius: 1,
                  mx: 1,
                  mt: 0.5,
                  mb: 1.5,

                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                    cursor: "pointer",
                  },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <BoldMessageText color={theme.palette.blue.main} size={25} />
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {t("Navbar.send_suggestions")}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="blue">
                      {t("Navbar.we_want_opinion")}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </a>
            {menuItems.map((item, i) =>
              item === "divider" ? (
                <Divider key={i} />
              ) : (
                <MenuItemWithIcon
                  key={i}
                  icon={item.icon}
                  label={item.label}
                  onClick={item.onClick}
                />
              )
            )}
          </Menu>
        </Toolbar>
      </Container>
    </AppBar>
  );
};
